import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import TableSortLabel from "@mui/material/TableSortLabel";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import TableHead from "@mui/material/TableHead";
import { visuallyHidden } from "@mui/utils";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import API from "../../config/axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ColorPicker from "../../components/ColorPicker";
import Tooltip from "@mui/material/Tooltip";
import "./CompanyList.css";
import { hasAccess } from "../../utils/utilities";
import { Switch } from "@mui/material";
import SearchBar from "../../components/SearchBar";
import Button from "../../components/CommonButton";
import CommonDialog from "../../components/CommonDialog";
import FormLabel from "../../components/FormLabel";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function CustomPaginationActionsTable() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");
  const [rows, setRows] = React.useState([]);
  const [totalRows, setTotalRows] = React.useState(10);
  const [search, setSearch] = React.useState("");
  const [addDialog, setAddDialog] = React.useState(false);
  const [editDialog, setEditDialog] = React.useState(false);
  const [deleteDialog, setDeleteDialog] = React.useState(false);
  const [editCompanyId, setEditCompanyId] = React.useState(null);
  const [deleteCompanyId, setDeleteCompanyId] = React.useState(null);
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [personName, setPersonName] = React.useState("");
  const [logo, setLogo] = React.useState(null);
  const [displayAlert, setDisplayAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertVariant, setAlertVariant] = React.useState("success");
  const [inputErrors, setInputErrors] = React.useState({});
  const [logoBgColor, setLogoBgColor] = React.useState({
    r: "240",
    g: "240",
    b: "240",
    a: "1",
  });
  const [logoPreview, setLogoPreview] = React.useState(null);
  const logoFileInputRef = React.useRef(null);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = rows.length === 0 ? 1 : 0;

  const displayAlertMessage = (variant, message) => {
    setDisplayAlert(true);
    setAlertMessage(message);
    setAlertVariant(variant);
  };

  const closeAlertMessage = () => {
    setDisplayAlert(false);
    setAlertMessage("");
  };

  const openFileExplorer = () => {
    if (logoFileInputRef && logoFileInputRef.current) {
      logoFileInputRef.current.click();
    }
  };

  const generateLogoPreview = (e) => {
    setInputErrors({ logo: false });
    if (e.target.files.length > 0) {
      if (["image/png", "image/jpeg"].includes(e.target.files[0].type)) {
        setLogoPreview(URL.createObjectURL(e.target.files[0]));
        setLogo(e.target.files[0]);
      } else {
        setLogoPreview(null);
        setLogo(null);
        setInputErrors({
          ...inputErrors,
          logo: "Invalid Extension. Only png, jpg, jpeg are allowed",
        });
      }
    } else {
      setLogoPreview(null);
      setLogo(null);
    }
  };

  const getCompanies = (searchValue = search) => {
    API.get("admin/companies", {
      params: {
        pagination: true,
        page: page + 1,
        rowsPerPage: rowsPerPage,
        sortBy: orderBy,
        descending: order === "asc" ? false : true,
        search: searchValue,
      },
    })
      .then((res) => {
        const data = res.data;

        if (data.status) {
          setRows(data.data.results);
          setTotalRows(data.data.totalResults || 0);
          setPage(data.data.page > data.data.totalPages ? 0 : data.data.page - 1);
        } else {
          console.log("something went wrong");
        }
      })
      .catch((error) => {
        console.log("error", error);
      });
  };

  const addCompany = () => {
    const formData = new FormData();

    formData.append("name", name || "");
    formData.append("logo", logo || "");
    formData.append("email", email || "");
    formData.append("person_name", personName || "");
    formData.append("color", JSON.stringify(logoBgColor) || "");

    API.post("admin/companies", formData).then((res) => {
      setInputErrors({});
      const data = res.data;

      if (data.status) {
        displayAlertMessage("success", "Company Added Successfully");
        handleAddDialogClose();
      } else {
        if (data && data.type === "validation" && data.error.length > 0) {
          data.error.forEach((error) => {
            setInputErrors((prevState) => {
              return { ...prevState, [error.field]: error.message };
            });
          });
        } else {
          displayAlertMessage("error", "Something went wrong");
          handleAddDialogClose();
        }
      }

      getCompanies();
    });
  };

  const editCompany = () => {
    const formData = new FormData();

    formData.append("name", name || "");
    formData.append("email", email || "");
    formData.append("person_name", personName || "");
    formData.append("logo", logo || "");
    formData.append("color", JSON.stringify(logoBgColor) || "");

    API.put(`admin/companies/${editCompanyId}`, formData)
      .then((res) => {
        const data = res.data;

        if (data.status) {
          displayAlertMessage("success", "Company Updated Successfully");
          handleEditDialogClose();
          getCompanies();
        } else {
          if (data && data.type === "validation" && data.error.length > 0) {
            data.error.forEach((error) => {
              setInputErrors((prevState) => {
                return { [error.field]: error.message, ...prevState };
              });
            });
          } else {
            displayAlertMessage("error", "Something went wrong");
            handleEditDialogClose();
          }
        }
      })
      .catch(() => {
        displayAlertMessage("error", "Something went wrong");
      });
  };

  const deleteCompany = () => {
    API.delete(`admin/companies/${deleteCompanyId}`)
      .then((res) => {
        const data = res.data;

        if (data.status) {
          displayAlertMessage("success", "Company Deleted Successfully");
          handleDeleteDialogClose();
          getCompanies();
        } else {
          displayAlertMessage("error", "Something went wrong");
        }
      })
      .catch(() => {
        displayAlertMessage("error", "Something went wrong");
      });
  };

  React.useEffect(() => {
    getCompanies();
  }, [page, order, orderBy, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddDialogClose = () => {
    setAddDialog(false);
    setInputErrors({});
    setName("");
    setEmail("");
    setPersonName("");
    setLogo(null);
    setLogoPreview(null);
    setLogoBgColor({ r: "240", g: "240", b: "240", a: "1" });
  };

  const handleEditDialogClose = () => {
    setEditDialog(false);
    setEditCompanyId(null);
    setInputErrors({});
    setName("");
    setEmail("");
    setPersonName("");
    setLogo(null);
    setLogoPreview(null);
    setLogoBgColor({ r: "240", g: "240", b: "240", a: "1" });
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialog(false);
    setDeleteCompanyId(null);
  };

  const showEditDialog = (data) => {
    setName(data.name);
    setPersonName(data?.admin?.name);
    setEmail(data?.admin?.email);
    setEditCompanyId(data.id);
    if (data.logo) {
      setLogoPreview(`${process.env.REACT_APP_IMAGE_URL}images/company/logo/${data.logo}`);
    }
    setLogoBgColor(data.color);
    setEditDialog(true);
  };

  const showDeleteConfirmationDialog = (id) => {
    setDeleteCompanyId(id);
    setDeleteDialog(true);
  };

  const createSortHandler = (property) => (event) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const toggleCompanyStatus = (companyId) => {
    API.put(`admin/companies/${companyId}/toggle-status`)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            getCompanies();
            displayAlertMessage("success", res.data.message);
          } else {
            if (res.data.type === "error") {
              displayAlertMessage("error", res.data.error?.message || "");
            } else {
              displayAlertMessage("error", "Something went wrong");
            }
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const headCells = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: "Company Name",
      sortable: true,
    },
    {
      id: "email",
      numeric: false,
      disablePadding: true,
      label: "Email",
      sortable: false,
    },
    {
      id: "person_name",
      numeric: false,
      disablePadding: true,
      label: "Person Name",
      sortable: false,
    },
    {
      id: "action",
      numeric: false,
      disablePadding: true,
      label: "Actions",
      sortable: false,
    },
  ];

  return (
    <div>
      <Grid container rowSpacing={0}>
        <Grid item xs={12} sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: "20px" }} color="#474747">
            Company Management
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          px={"25px"}
          sx={{ borderRadius: "10px", backgroundColor: "white", py: 3 }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="stretch"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box sx={{ display: "flex", alignItems: "stretch", gap: 2 }}>
              <SearchBar
                sx={{ width: "100%" }}
                onSearch={(search) => {
                  setSearch(search);
                  getCompanies(search);
                }}
              />
              {/* <TextField
                id="outlined-search"
                size="small"
                placeholder="Search"
                type="search"
                className="custom-input"
                sx={{
                  width: isMobile ? 2 / 3 : "100%",
                }}
                onChange={(e) => {
                  debounceSearch(e.target.value, rowsPerPage, page);
                  setSearch(e.target.value);
                }}
                value={search}
              /> */}
            </Box>
            {hasAccess() && <Button onClick={() => setAddDialog(true)}>Add Company</Button>}
          </Stack>

          <TableContainer component={Paper} elevation={0}>
            <Table aria-label="custom pagination table" className={"custom-table br-0"}>
              <TableHead sx={{ backgroundColor: "#f4f6f9" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "80px",
                      minWidth: "80px",
                      textAlign: "center",
                    }}
                  >
                    No.
                  </TableCell>
                  {headCells
                    .filter((h) => (hasAccess() ? {} : h.id !== "action"))
                    .map((headCell) => (
                      <TableCell
                        size="small"
                        key={headCell.id}
                        align={headCell.numeric ? "right" : "left"}
                        sortDirection={orderBy === headCell.id ? order : false}
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                          >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc" ? "sorted descending" : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        ) : (
                          headCell.label
                        )}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell style={{ textAlign: "center" }}>
                      {page * rowsPerPage + (index + 1)}
                    </TableCell>
                    <TableCell size="small" component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <TableCell size="small" component="th" scope="row">
                      {row?.admin?.email}
                    </TableCell>
                    <TableCell size="small" component="th" scope="row">
                      {row?.admin?.name}
                    </TableCell>
                    {hasAccess() && (
                      <TableCell size="small" style={{ width: 160 }} align="left">
                        <Switch
                          color="primary"
                          checked={!row.is_disabled}
                          onChange={() => toggleCompanyStatus(row.id)}
                        />
                        <IconButton
                          size="small"
                          aria-label="edit"
                          color="inherit"
                          onClick={() => showEditDialog(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="edit"
                          color="inherit"
                          onClick={() => showDeleteConfirmationDialog(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}

                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell size="small" colSpan={6}>
                      <Typography sx={{ width: "100%", textAlign: "center" }}>
                        No Data Available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                    colSpan={5}
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <CommonDialog
        title={"Add Company"}
        open={addDialog}
        onClose={handleAddDialogClose}
        onSubmit={addCompany}
      >
        <Paper style={{ width: "100%" }} elevation={0}>
          <Box style={{ padding: "10px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <FormLabel title="Company Name" required />
                <TextField
                  id="name"
                  placeholder="Enter Company Name"
                  value={name}
                  autoFocus
                  fullWidth
                  size="small"
                  onChange={(e) => setName(e.target.value)}
                  error={inputErrors.name ? true : false}
                  helperText={inputErrors.name || ""}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <FormLabel title="Email" required />
                <TextField
                  placeholder="Enter Email"
                  size="small"
                  value={email || ""}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  error={inputErrors.email ? true : false}
                  helperText={inputErrors.email || ""}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <FormLabel title="Person Name" required />
                <TextField
                  placeholder="Enter Person Name"
                  size="small"
                  value={personName || ""}
                  onChange={(e) => setPersonName(e.target.value)}
                  fullWidth
                  error={inputErrors.person_name ? true : false}
                  helperText={inputErrors.person_name || ""}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <FormLabel title="Logo" required />
                  <Tooltip title="Logo Background">
                    <Box>
                      <ColorPicker
                        color={logoBgColor}
                        setColor={setLogoBgColor}
                        popoverStyle={{ top: 10, right: 80 }}
                        colorBoxStyle={{ height: 20 }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
                <Box>
                  <div
                    className="logo_container"
                    style={{
                      backgroundColor: `rgba(${logoBgColor.r}, ${logoBgColor.g},${logoBgColor.b}, ${logoBgColor.a})`,
                    }}
                  >
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="company logo"
                        height={100}
                        width={226}
                        style={{ cursor: "pointer", objectFit: "contain" }}
                        onClick={openFileExplorer}
                      />
                    ) : (
                      <div className="logo" onClick={openFileExplorer}>
                        Select Logo
                      </div>
                    )}
                  </div>
                  {inputErrors.logo ? (
                    <span style={{ fontSize: "13px", color: "red" }}>{inputErrors.logo}</span>
                  ) : (
                    ""
                  )}
                </Box>
                <TextField
                  id="logo"
                  placeholder="select logo"
                  type="file"
                  size="small"
                  onChange={generateLogoPreview}
                  fullWidth
                  sx={{ display: "none" }}
                  inputRef={logoFileInputRef}
                  error={inputErrors.logo ? true : false}
                  helperText={inputErrors.logo || " "}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </CommonDialog>

      <CommonDialog
        title={"Edit Company"}
        open={editDialog}
        onClose={handleEditDialogClose}
        onSubmit={editCompany}
      >
        <Paper style={{ width: "100%" }} elevation={0}>
          <Box style={{ padding: "10px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <FormLabel title="Company Name" />
                <TextField
                  id="name"
                  placeholder="Name"
                  value={name}
                  autoFocus
                  size="small"
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  error={inputErrors.name ? true : false}
                  helperText={inputErrors.name || ""}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <FormLabel title="Company Email" />
                <TextField
                  placeholder="Email"
                  size="small"
                  value={email || ""}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  error={inputErrors.email ? true : false}
                  helperText={inputErrors.email || ""}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <FormLabel title="Person Name" />
                <TextField
                  placeholder="Person Name"
                  size="small"
                  value={personName || ""}
                  onChange={(e) => setPersonName(e.target.value)}
                  fullWidth
                  error={inputErrors.person_name ? true : false}
                  helperText={inputErrors.person_name || ""}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <FormLabel title="Logo" />
                  <ColorPicker
                    color={logoBgColor}
                    setColor={setLogoBgColor}
                    popoverStyle={{ top: 10, right: 80 }}
                    colorBoxStyle={{ height: 20 }}
                  />
                </Box>
                <div
                  className="logo_container"
                  style={{
                    backgroundColor: `rgba(${logoBgColor.r}, ${logoBgColor.g},${logoBgColor.b}, ${logoBgColor.a})`,
                  }}
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="company logo"
                      height={100}
                      width={226}
                      style={{ cursor: "pointer", objectFit: "contain" }}
                      onClick={openFileExplorer}
                    />
                  ) : (
                    <div className="logo" onClick={openFileExplorer}>
                      Select Logo
                    </div>
                  )}
                </div>
                {inputErrors.logo ? (
                  <span style={{ fontSize: "13px", color: "red" }}>{inputErrors.logo}</span>
                ) : (
                  ""
                )}
                <TextField
                  id="logo"
                  placeholder="select logo"
                  type="file"
                  size="small"
                  onChange={generateLogoPreview}
                  fullWidth
                  sx={{ display: "none" }}
                  inputRef={logoFileInputRef}
                  error={inputErrors.logo ? true : false}
                  helperText={inputErrors.logo || ""}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </CommonDialog>

      <DeleteConfirmationDialog
        name={"this company"}
        open={deleteDialog}
        onClose={handleDeleteDialogClose}
        onDelete={deleteCompany}
      />

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={displayAlert}
        onClose={closeAlertMessage}
        message={displayAlert}
        key={"system-message"}
        autoHideDuration={2000}
      >
        <Alert severity={alertVariant}>
          {alertMessage}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            sx={{ ml: 2 }}
            onClick={closeAlertMessage}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>
    </div>
  );
}
